import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import theme from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import { AuthProvider } from '../../contexts/AuthContext';
import { PermissionsProvider } from '../../contexts/PermissionsContext';
import ProtectedRoute from '../auth/ProtectedRoute';

// PM Components
import PMActiveProjects from '../pm/ActiveProjects';
import PMTeamManagement from '../pm/TeamManagement';
import PMProjectTimeline from '../pm/ProjectTimeline';
import PMCommunicationCenter from '../pm/CommunicationCenter';
import PMCommunications from '../pm/Communications';
import PMProjectAnalytics from '../pm/ProjectAnalytics';
import PMFilesManagement from '../pm/FilesManagement';
import PMQATesting from '../pm/QATesting';
import PMReports from '../pm/Reports';
import PMSettings from '../pm/Settings';
import { LoadingProvider } from '../../contexts/LoadingContext';
import { NotificationProvider as LegacyNotificationProvider } from '../../contexts/NotificationContext';
import { NotificationProvider as NotificationSystemProvider } from '../ui/NotificationSystem';
import { ImpersonationBanner, useImpersonation } from '../common/ImpersonationBanner';

// Marketing Pages
import HomePage from '../marketing/HomePage';
import ServicesPage from '../marketing/ServicesPage';
import AboutPage from '../marketing/AboutPage';
import ContactPage from '../pages/ContactPage';

// Authentication Pages
import LoginPage from '../auth/LoginPage';
import RegisterPage from '../auth/RegisterPage';
import ForgotPasswordPage from '../auth/ForgotPasswordPage';
import QuickLogin from '../auth/QuickLogin';

// New Phase 1 Authentication Components
import UserProfileManager from '../auth/UserProfileManager';
import SessionManagementPage from '../auth/SessionManagementPage';
import SessionTimeoutWarning from '../auth/SessionTimeoutWarning';
import MFASetupComponent from '../auth/MFASetupComponent';
import MFAVerificationComponent from '../auth/MFAVerificationComponent';
import EnhancedRegistrationForm from '../auth/EnhancedRegistrationForm';
import PasswordResetRequest from '../auth/PasswordResetRequest';
import PasswordResetForm from '../auth/PasswordResetForm';
import EmailVerification from '../auth/EmailVerification';
import SecurityDashboard from '../auth/SecurityDashboard';

// Debug Pages
import AuthStatus from '../debug/AuthStatus';
import OnboardingDebug from '../debug/OnboardingDebug';

// Onboarding - Unified registration and onboarding flow
import UnifiedOnboardingWizard from '../onboarding/UnifiedOnboardingWizard';

// Client Components
import ClientDashboard from '../client/ClientDashboard';
import ClientProjects from '../client/projects/ClientProjects';
import ActiveProjects from '../client/projects/ActiveProjects';
import ProjectTimeline from '../client/projects/ProjectTimeline';
import ProjectGallery from '../client/projects/ProjectGallery';
import ClientCommunication from '../client/communication/ClientCommunication';
import Messages from '../client/communication/Messages';
import ProjectManagerDashboard from '../dashboards/ProjectManagerDashboard';
import AdminDashboard from '../dashboards/AdminDashboard';
import DeveloperDashboard from '../dashboards/DeveloperDashboard';
import NotFoundPage from '../common/NotFoundPage';
import ProjectOverview from '../../pages/ProjectOverview';

// Admin Components
import ProjectManagement from '../admin/ProjectManagement';
import CreateProjectPage from '../admin/projects/CreateProjectPage';
import CreateProjectPageEnhanced from '../admin/projects/CreateProjectPageEnhanced';
import ImportProjectPage from '../../pages/admin/ImportProjectPage';
import ProjectTemplatesPage from '../admin/projects/ProjectTemplatesPage';
import AdminProjectDetailsPage from '../admin/projects/AdminProjectDetailsPage';
import AdminProjectEditPage from '../admin/projects/AdminProjectEditPage';
import ClientManagement from '../admin/ClientManagement';
import AddClientPage from '../admin/clients/AddClientPage';
import ClientPortalAccessPage from '../admin/clients/ClientPortalAccessPage';
import FinancialManagement from '../admin/FinancialManagement';
import Analytics from '../admin/Analytics';
import SystemSettings from '../admin/SystemSettings';
import InvoicesPage from '../admin/financial/InvoicesPage';
import PaymentsPage from '../admin/financial/PaymentsPage';
import RevenueAnalyticsPage from '../admin/financial/RevenueAnalyticsPage';
import FinancialReportsPage from '../admin/analytics/FinancialReportsPage';
import AnalyticsAndReportsPage from '../admin/analytics/AnalyticsAndReportsPage';
import IntegrationsPage from '../admin/settings/IntegrationsPage';
import SecurityPage from '../admin/settings/SecurityPage';
import TwilioSettings from '../admin/TwilioSettings';

// User Management Components
import AllUsersPage from '../admin/users/AllUsersPage';
import CreateUserPage from '../admin/users/CreateUserPage';
import EditUserPage from '../admin/users/EditUserPage';
import RolesPermissionsPage from '../admin/users/RolesPermissionsPage';
import TeamsPage from '../admin/users/TeamsPage';

// SpecKit Components
import EnhancedSpecKitDashboard from '../EnhancedSpecKitDashboard';

// Estimation Components (Phase 3)
import ClientEstimationPage from '../../pages/ClientEstimationPage';
import AdminEstimationPage from '../../pages/AdminEstimationPage';

// Developer Pages
import DeveloperTasksPage from '../../pages/developer/DeveloperTasksPage';
import DeveloperProjectsPage from '../../pages/developer/DeveloperProjectsPage';
import DeveloperToolsPage from '../../pages/developer/DeveloperToolsPage';

// System Monitoring
import SystemMonitor from '../admin/SystemMonitor';
import DeveloperBugsPage from '../../pages/developer/DeveloperBugsPage';

// PM Dashboard Main Page
import PMDashboard from '../../pages/pm/PMDashboard';

// Dashboard Layouts
import DashboardLayout from '../layouts/DashboardLayout';
import MarketingLayout from '../layouts/MarketingLayout';

// Role-based routing
import RoleDashboardRedirect from './RoleDashboardRedirect';

/**
 * Router content wrapper that provides impersonation banner inside Router context
 */
const RouterContent: React.FC = () => {
  const { status, handleStopImpersonation } = useImpersonation();
  
  return (
    <Box>
      <ImpersonationBanner 
        status={status} 
        onStopImpersonation={handleStopImpersonation} 
      />
      <Routes>
            {/* Marketing Website Routes */}
            <Route path="/" element={<MarketingLayout />}>
              <Route index element={<HomePage />} />
              <Route path="services" element={<ServicesPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="contact" element={<ContactPage />} />
              
              {/* Legacy redirects */}
              <Route path="home" element={<Navigate to="/" replace />} />
              <Route path="service" element={<Navigate to="/services" replace />} />
              <Route path="about-us" element={<Navigate to="/about" replace />} />
              <Route path="contact-us" element={<Navigate to="/contact" replace />} />
            </Route>

            {/* Authentication Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<UnifiedOnboardingWizard />} />
            <Route path="/register/simple" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<PasswordResetRequest />} />
            <Route path="/reset-password" element={<PasswordResetForm />} />
            <Route path="/verify-email" element={<EmailVerification />} />
            <Route path="/quick-login" element={<QuickLogin />} />
            <Route path="/auth-status" element={<AuthStatus />} />
            <Route path="/debug/onboarding" element={<OnboardingDebug />} />
            
            {/* MFA Routes */}
            <Route 
              path="/mfa/setup" 
              element={
                <ProtectedRoute>
                  <MFASetupComponent />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/mfa/verify" 
              element={
                <MFAVerificationComponent 
                  email="user@example.com" 
                  onVerificationSuccess={(token) => console.log('MFA verified:', token)}
                />
              } 
            />

            {/* User Management Routes */}
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <UserProfileManager />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sessions" 
              element={
                <ProtectedRoute>
                  <SessionManagementPage />
                </ProtectedRoute>
              } 
            />

            {/* Client Onboarding - now redirects to main registration which includes onboarding */}
            <Route 
              path="/onboarding" 
              element={<Navigate to="/register" replace />}
            />

            {/* Protected Dashboard Routes */}
            <Route 
              path="/dashboard/*" 
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Client Dashboard */}
              <Route 
                path="client/*" 
                element={
                  <ProtectedRoute allowedRoles={['client']}>
                    <Routes>
                      <Route index element={<ClientDashboard onError={(error) => console.error(error)} />} />
                      
                      {/* Projects Routes */}
                      <Route path="projects" element={<ClientProjects />}>
                        <Route index element={<ActiveProjects />} />
                        <Route path="active" element={<ActiveProjects />} />
                        <Route path="timeline" element={<ProjectTimeline />} />
                        <Route path="gallery" element={<ProjectGallery />} />
                      </Route>
                      <Route path="projects/:projectId" element={<ProjectOverview />} />
                      
                      {/* Estimation Routes (Phase 3) */}
                      <Route path="estimations" element={<ClientEstimationPage />} />
                      <Route path="estimations/:id" element={<ClientEstimationPage />} />
                      
                      {/* Communication Routes */}
                      <Route path="communication" element={<ClientCommunication />}>
                        <Route index element={<Messages />} />
                        <Route path="messages" element={<Messages />} />
                        <Route path="calls" element={<div>Video Calls - Coming Soon</div>} />
                        <Route path="updates" element={<div>Project Updates - Coming Soon</div>} />
                      </Route>
                      
                      {/* Billing Routes */}
                      <Route path="billing/invoices" element={<div>Invoices - Coming Soon</div>} />
                      <Route path="billing/payments" element={<div>Payment History - Coming Soon</div>} />
                      <Route path="billing/settings" element={<div>Billing Settings - Coming Soon</div>} />
                      
                      {/* Files Routes */}
                      <Route path="files/projects" element={<div>Project Files - Coming Soon</div>} />
                      <Route path="files/downloads" element={<div>Downloads - Coming Soon</div>} />
                      <Route path="files/sharing" element={<div>File Sharing - Coming Soon</div>} />
                      
                      {/* Other Client Routes */}
                      <Route path="support" element={<div>Support - Coming Soon</div>} />
                      <Route path="feedback" element={<div>Feedback - Coming Soon</div>} />
                      <Route path="settings" element={<div>Account Settings - Coming Soon</div>} />
                    </Routes>
                  </ProtectedRoute>
                } 
              />
              
              {/* Project Manager Dashboard */}
              <Route 
                path="pm/*" 
                element={
                  <ProtectedRoute allowedRoles={['project_manager']}>
                    <Routes>
                      <Route index element={<PMDashboard />} />
                      
                      {/* Projects Routes */}
                      <Route path="projects" element={<PMActiveProjects />} />
                      <Route path="projects/active" element={<PMActiveProjects />} />
                      <Route path="projects/timeline" element={<PMProjectTimeline />} />
                      <Route path="projects/planning" element={<PMProjectTimeline />} />
                      <Route path="projects/resources" element={<PMTeamManagement />} />
                      <Route path="projects/:projectId" element={<ProjectOverview />} />
                      
                      {/* Team Management Routes */}
                      <Route path="team" element={<PMTeamManagement />} />
                      <Route path="team/tasks" element={<PMTeamManagement />} />
                      <Route path="team/performance" element={<PMTeamManagement />} />
                      <Route path="team/time" element={<PMTeamManagement />} />
                      
                      {/* Client Communication Routes */}
                      <Route path="clients" element={<PMCommunicationCenter />} />
                      <Route path="clients/portal" element={<PMCommunicationCenter />} />
                      <Route path="clients/updates" element={<PMCommunicationCenter />} />
                      <Route path="clients/meetings" element={<PMCommunicationCenter />} />
                      
                      {/* Communications System - Multi-channel */}
                      <Route path="communications" element={<PMCommunications />} />
                      
                      {/* Analytics Routes */}
                      <Route path="analytics" element={<PMProjectAnalytics />} />
                      <Route path="analytics/progress" element={<PMProjectAnalytics />} />
                      <Route path="analytics/productivity" element={<PMProjectAnalytics />} />
                      <Route path="analytics/budget" element={<PMProjectAnalytics />} />
                      
                      {/* Other Routes */}
                      <Route path="files" element={<PMFilesManagement />} />
                      <Route path="qa" element={<PMQATesting />} />
                      <Route path="reports" element={<PMReports />} />
                      <Route path="settings" element={<PMSettings />} />
                      
                      {/* Legacy routes for compatibility */}
                      <Route path="client-progress" element={<PMCommunicationCenter />} />
                      <Route path="messages" element={<PMCommunicationCenter />} />
                    </Routes>
                  </ProtectedRoute>
                } 
              />
              
              {/* Admin Dashboard */}
              <Route 
                path="admin/*" 
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <Routes>
                      <Route index element={<AdminDashboard />} />
                      <Route path="projects" element={<ProjectManagement />} />
                      <Route path="projects/:projectId" element={<AdminProjectDetailsPage />} />
                      <Route path="projects/:projectId/edit" element={<AdminProjectEditPage />} />
                      <Route path="projects/:projectId/tasks" element={<ProjectOverview />} />
                      <Route path="projects/create" element={<CreateProjectPageEnhanced />} />
                      <Route path="projects/import" element={<ImportProjectPage />} />
                      <Route path="projects/templates" element={<ProjectTemplatesPage />} />
                      <Route path="projects/speckit" element={<EnhancedSpecKitDashboard projectId={1} projectName="Demo Project" />} />
                      
                      {/* Estimation Management Routes (Phase 3) */}
                      <Route path="estimations" element={<AdminEstimationPage />} />
                      <Route path="estimations/:id" element={<AdminEstimationPage />} />
                      
                      <Route path="clients" element={<ClientManagement />} />
                      <Route path="clients/create" element={<AddClientPage />} />
                      <Route path="clients/portal" element={<ClientPortalAccessPage />} />
                      <Route path="financial" element={<FinancialManagement />} />
                      <Route path="financial/invoices" element={<InvoicesPage />} />
                      <Route path="financial/payments" element={<PaymentsPage />} />
                      <Route path="financial/analytics" element={<RevenueAnalyticsPage />} />
                      <Route path="users" element={<AllUsersPage />} />
                      <Route path="users/create" element={<CreateUserPage />} />
                      <Route path="users/edit/:userId" element={<EditUserPage />} />
                      <Route path="users/roles" element={<RolesPermissionsPage />} />
                      <Route path="users/teams" element={<TeamsPage />} />
                      <Route path="analytics" element={<Analytics />} />
                      <Route path="analytics/dashboard" element={<AnalyticsAndReportsPage />} />
                      <Route path="analytics/reports" element={<FinancialReportsPage />} />
                      <Route path="analytics/performance" element={<div>Performance Analytics Page</div>} />
                      <Route path="analytics/usage" element={<div>Usage Analytics Page</div>} />
                      <Route path="settings" element={<SystemSettings />} />
                      <Route path="settings/security" element={<SecurityPage />} />
                      <Route path="settings/integrations" element={<IntegrationsPage />} />
                      <Route path="settings/twilio" element={<TwilioSettings />} />
                      <Route path="settings/backup" element={<div>Backup & Recovery Page</div>} />
                      <Route path="settings/api" element={<div>API Configuration Page</div>} />
                      
                      {/* System Monitoring Routes */}
                      <Route path="monitor" element={<SystemMonitor />} />
                      <Route path="help" element={<div>Help & Support</div>} />
                    </Routes>
                  </ProtectedRoute>
                } 
              />
              
              {/* Developer Dashboard */}
              <Route 
                path="developer/*" 
                element={
                  <ProtectedRoute allowedRoles={['developer']}>
                    <Routes>
                      <Route index element={<DeveloperDashboard />} />
                      
                      {/* Tasks Routes */}
                      <Route path="tasks/*" element={<DeveloperTasksPage />} />
                      
                      {/* Projects Routes */}
                      <Route path="projects/*" element={<DeveloperProjectsPage />} />
                      <Route path="projects/:projectId" element={<ProjectOverview />} />
                      
                      {/* Development Tools Routes */}
                      <Route path="tools/*" element={<DeveloperToolsPage />} />
                      
                      {/* Bug Tracking Routes */}
                      <Route path="bugs/*" element={<DeveloperBugsPage />} />
                      
                      {/* Other Routes - Placeholders */}
                      <Route path="analytics" element={<Box sx={{ p: 4 }}><Typography variant="h5">Code Analytics - Coming Soon</Typography></Box>} />
                      <Route path="knowledge" element={<Box sx={{ p: 4 }}><Typography variant="h5">Knowledge Base - Coming Soon</Typography></Box>} />
                      <Route path="performance" element={<Box sx={{ p: 4 }}><Typography variant="h5">Performance Monitor - Coming Soon</Typography></Box>} />
                    </Routes>
                  </ProtectedRoute>
                } 
              />
              
              {/* Default redirect to user's appropriate dashboard based on role */}
              <Route index element={<RoleDashboardRedirect />} />
              
              {/* User Profile and Security Routes */}
              <Route path="profile" element={<UserProfileManager />} />
              <Route path="sessions" element={<SessionManagementPage />} />
              <Route path="security" element={<SecurityDashboard />} />
              <Route path="mfa/setup" element={<MFASetupComponent />} />
            </Route>

            {/* 404 fallback */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Box>
      );
    };

/**
 * Main application router component
 * Handles routing for marketing website and authenticated dashboard areas
 */
const AppRouter: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <PermissionsProvider>
          <LoadingProvider>
            <LegacyNotificationProvider>
              <NotificationSystemProvider>
                <Router>
                  <RouterContent />
                </Router>
              </NotificationSystemProvider>
            </LegacyNotificationProvider>
          </LoadingProvider>
        </PermissionsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default AppRouter;
